import { useMutation } from 'modules/core/hooks/useMutation'
import {
  ShiftManagementScheduleBulkDropDocument,
  ShiftsRestDayFragmentDoc,
  ShiftsShiftFragmentDoc,
} from 'generated/resources/graphql'
import type RestDay from 'modules/shift_management/store/internal/models/RestDay'
import type Shift from 'modules/shift_management/store/internal/models/Shift'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { removeDestroyedRestDaysCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedRestDaysCache'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'
import { updateCreatedRestDaysCache } from 'modules/shift_management/utils/apolloCache/updateCreatedRestDaysCache'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'

type SchedulePasteItem = {
  sourceShifts: Shift[]
  sourceRestDays: RestDay[]
  targetRowId: string
  targetDate: string
  mode: 'copy' | 'move'
  removeTargetItems: boolean
  dropOverrides?: {
    startTime?: string | null
    locationId?: number | null
    workAreaId?: number | null
  }
}

const shiftCacheId = (id: string) => `ShiftManagementShift:${id}`
const restDayCacheId = (id: string) => `ShiftManagementRestDay:${id}`

const useBulkDropSchedule = () => {
  const [{ view }] = useSchedule()

  const [bulkDropSchedule] = useMutation(ShiftManagementScheduleBulkDropDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDropShiftmanagementSchedule,
    update: (cache, { data }) => {
      const schedule = data?.shiftManagementMutations.bulkDropShiftmanagementSchedule.schedule
      if (!schedule) return

      const droppedShifts = schedule.shifts ?? []
      const droppedRestDays = schedule.restDays ?? []
      const deletedShifts = schedule.deletedShiftIds.flatMap((id) => {
        const shift = cache.readFragment({
          id: shiftCacheId(id),
          fragment: ShiftsShiftFragmentDoc,
          fragmentName: 'ShiftsShift',
        })

        return shift ? [shift] : []
      })
      const deletedRestDays = schedule.deletedRestDayIds.flatMap((id) => {
        const restDay = cache.readFragment({
          id: restDayCacheId(id),
          fragment: ShiftsRestDayFragmentDoc,
          fragmentName: 'ShiftsRestDay',
        })

        return restDay ? [restDay] : []
      })

      removeDestroyedShiftsCache({
        cache,
        view,
        shifts: deletedShifts,
      })

      removeDestroyedRestDaysCache({
        cache,
        view,
        restDays: deletedRestDays,
      })

      updateCreatedShiftsCache({
        cache,
        view,
        shifts: droppedShifts,
      })

      updateCreatedRestDaysCache({
        cache,
        view,
        restDays: droppedRestDays,
      })
    },
  })

  return bulkDropSchedule
}

export { useBulkDropSchedule }
export type { SchedulePasteItem }
